/**
 * 供給地点マスタ (CIS契約マスタ) CSV の列ヘッダー (列順固定、
 * docs/specs/admin/data/uploads/supply-point-master.md §1 / AC-DUI-001)。
 *
 * アップロード側の厳格ヘッダー検証 (`CisContractCsvUpload`) とダウンロード側の
 * CSV 組み立て (`AdminUploads`) の 2 箇所が同じ列定義を要求するため、
 * `src/models/` に置く単一 SoT とする (列がどちらか片方だけでずれると
 * アップロード → ダウンロード → 再アップロードの往復が壊れる)。
 *
 * アップロードは 7 列 (旧形式) / 8 列 (事業者コード付き) × 日本語 / 英語
 * ヘッダーの計 4 パターンを受理する (AC-DUI-001、personal-settings.md
 * AC-SPS-031)。8 列形式は 事業者コード が delete の手前に入る (backend
 * `usecase/ciscontract/csv.go` の csvHeaderWithOwner と同じ列順)。
 * テンプレート DL / 入稿済みデータ DL は 8 列形式に寄せ (2026-08-25 PdM 確定、
 * issue #6105、根拠は common.md §6 の「アップロード時と同一の固定列構成」規定 +
 * 洗い替え運用)、表示言語に応じて日本語 / 英語のヘッダー・区分値で出す
 * (common.md §6、`conventions.md`「CSV の英語表示」)。
 */
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import { escapeCsvCell } from '~/libs/csv'
import type { AcceptedCsvHeaders } from '~/models/csvAcceptedHeaders'

/**
 * 8 列形式 (事業者コード付き、日本語) の列名。`CIS_CONTRACT_CSV_HEADER` と
 * `serializeCisContractCsvTemplateRows` / `serializeCisContractCsvExportRows`
 * の単一 SoT (bilateral 側の `BILATERAL_TRADE_CSV_COLUMNS` →
 * `BILATERAL_TRADE_CSV_HEADER` / `serializeBilateralTradeCsvRows` と同じ構造。
 * `~/models/bilateralTradeCsv.ts` 参照)。列を増減する際はここと
 * `CIS_CONTRACT_CSV_COLUMNS_EN` (英語列名。言語が違うため同じ配列から導出
 * できない) の 2 箇所を直す — ただし各配列からヘッダー文字列自体は自動導出
 * されるため、「配列を直したのにヘッダー文字列が古いまま」という片側だけ
 * 取り残される事故は構造的に起きない (issue #6467 レビュー指摘:
 * 導入時に `CIS_CONTRACT_CSV_HEADER_EN` だけ手書き文字列のまま残り、
 * ja 側だけ配列から自動追従して en 側が取り残されていた)。
 */
export const CIS_CONTRACT_CSV_COLUMNS = [
  '供給地点特定番号',
  'エリア',
  '契約種別コード',
  '電圧階級',
  '開始日',
  '終了日',
  '事業者コード',
  'delete',
] as const

export type CisContractCsvColumn = (typeof CIS_CONTRACT_CSV_COLUMNS)[number]

/** 8 列形式の日本語ヘッダー (日本語表示のテンプレート DL / 入稿済みデータ DL)。 */
export const CIS_CONTRACT_CSV_HEADER = CIS_CONTRACT_CSV_COLUMNS.join(',')

/**
 * 8 列形式 (事業者コード付き、英語) の列名 (spec §1 列定義表の英語ヘッダー名、
 * AC-SPS-031)。旧名 `AreaCode` / `BusinessCd` は移行期間なしで改名した
 * (`admin/data/uploads/csv-columns.md`「英語ヘッダー名」)。BusinessCode は Delete
 * の手前 — 列順は `CIS_CONTRACT_CSV_COLUMNS` (日本語) と完全一致させる
 * (backend `usecase/ciscontract/csv.go` の csvHeaderWithOwner と同じ列順)。
 */
export const CIS_CONTRACT_CSV_COLUMNS_EN = [
  'SupplyPointSpecificNumber',
  'Area',
  'CisTariffCode',
  'VoltageClass',
  'StartDate',
  'EndDate',
  'BusinessCode',
  'Delete',
] as const

/** テンプレート DL / 入稿済みデータ DL の列構成 (8 列形式、英語)。 */
export const CIS_CONTRACT_CSV_HEADER_EN = CIS_CONTRACT_CSV_COLUMNS_EN.join(',')

/**
 * 雛形 DL / 入稿済みデータ DL のヘッダー行 (表示言語ごと)。英語表示では英語
 * ヘッダー名で出す (`admin/data/uploads/common.md` §6)。列順は日英で同じなので、
 * 行の値は日本語の列名を key にしたまま出力できる。
 */
function cisContractCsvHeaderFor(isEnglish: boolean): string {
  return isEnglish ? CIS_CONTRACT_CSV_HEADER_EN : CIS_CONTRACT_CSV_HEADER
}

/**
 * データ種別名 (ダウンロードファイル名の `{データ種別名}`、`common.md` §6 の対応表)。
 * 雛形 (`CisContractCsvUpload`) と入稿済みデータ (`AdminUploads`) の 2 page が使う。
 */
export function cisContractCsvDataTypeName(isEnglish: boolean): string {
  return isEnglish ? 'supply-point-master' : '供給地点マスタ'
}

/** 雛形のファイル名 (`{データ種別名}_テンプレート.csv` / 英語表示は `_template.csv`)。 */
export function cisContractCsvTemplateFileName(isEnglish: boolean): string {
  return `${cisContractCsvDataTypeName(isEnglish)}_${isEnglish ? 'template' : 'テンプレート'}.csv`
}

/**
 * 雛形 DL 向け serializer (common.md AC-DUC-031)。事業者コード・delete 等、
 * 記入例が省略する列を許容するため `Partial` で受ける
 * (`~/pages/CisContractCsvUpload/models/cisContractCsvColumnGuide` が使う)。
 * 区切りはカンマ、改行は `\r\n` (RFC 4180)、末尾に改行は追加しない
 * (`serializeCisContractCsvExportRows` と同じ規約。どちらの改行規約かは
 * 各 serializer 自身のテスト (`cisContractCsvHeader.test.ts`) が literal で
 * 固定する — issue #6467 レビュー指摘: 両者が非 export の共通実装
 * `serializeRows` に委譲する形は、`serializeCisContractCsvTemplateRows` 自身が
 * `serializeRows` と同一シグネチャ〈Partial〉で export されている以上
 * 「非 export 化で型により守る」構造になっておらず、規約の一致は本来
 * テストで固定すべきものだった)。
 */
export function serializeCisContractCsvTemplateRows(
  rows: ReadonlyArray<Partial<Record<CisContractCsvColumn, string>>>,
  isEnglish: boolean,
): string {
  const lines = [cisContractCsvHeaderFor(isEnglish)]
  for (const row of rows) {
    lines.push(
      CIS_CONTRACT_CSV_COLUMNS.map((col) => escapeCsvCell(row[col] ?? '')).join(
        ',',
      ),
    )
  }
  return lines.join('\r\n')
}

/**
 * 入稿済みデータのエクスポート向け serializer (common.md §6 AC-DUC-028/029)。
 * proto 由来の値を全列マッピングする必要があるため、全列必須の型 (`Record`。
 * `Partial` ではない) で受ける (`~/pages/AdminUploads/models/cisContractCsvExport.ts`
 * が使う)。列を増やす際は `CIS_CONTRACT_CSV_COLUMNS` を直すだけでなく、この
 * 関数の呼び出し側の row 組み立ても型エラーとして必ず追従を要求される
 * (`row[col]` は `Partial` の `?? ''` フォールバックを持たない — 全列が
 * 埋まっている前提を型で強制する)。
 */
export function serializeCisContractCsvExportRows(
  rows: ReadonlyArray<Record<CisContractCsvColumn, string>>,
  isEnglish: boolean,
): string {
  const lines = [cisContractCsvHeaderFor(isEnglish)]
  for (const row of rows) {
    lines.push(
      CIS_CONTRACT_CSV_COLUMNS.map((col) => escapeCsvCell(row[col])).join(','),
    )
  }
  return lines.join('\r\n')
}

/** 旧 7 列形式 (事業者コード列なし、日本語)。アップロードのみ後方互換で受理する。 */
export const CIS_CONTRACT_CSV_HEADER_LEGACY =
  '供給地点特定番号,エリア,契約種別コード,電圧階級,開始日,終了日,delete'

/** 旧 7 列形式 (英語ヘッダー、AC-SPS-031)。アップロードのみ後方互換で受理する。 */
export const CIS_CONTRACT_CSV_HEADER_EN_LEGACY =
  'SupplyPointSpecificNumber,Area,CisTariffCode,VoltageClass,StartDate,EndDate,Delete'

/**
 * アップロードが受理するヘッダーの全形式 (7 列 / 8 列 × 日本語 / 英語。
 * `precheckCisContractCsv` が参照する単一 SoT)。照合規則 (日本語は完全一致、英語は
 * 大文字・小文字を区別しない) は `AcceptedCsvHeaders` で、backend `ciscontract/csv.go`
 * の validateHeader と同じ判定。
 */
export const CIS_CONTRACT_CSV_ACCEPTED_HEADERS: AcceptedCsvHeaders = {
  ja: [CIS_CONTRACT_CSV_COLUMNS, CIS_CONTRACT_CSV_HEADER_LEGACY.split(',')],
  en: [
    CIS_CONTRACT_CSV_COLUMNS_EN,
    CIS_CONTRACT_CSV_HEADER_EN_LEGACY.split(','),
  ],
}

/**
 * `エリア` 列の値 (10 エリア。`conventions.md`「エリア一覧と表示順序」の日本語・英語
 * 表示。CSV の区分値も同じ)。画面の表示ラベル (`convertAreaLabel` が引く i18n の
 * `common:area.*`) とは別に、取込が受理する CSV の値として持つ (backend
 * `valueobject.AreaCodeFromCsvLabel` が受理する値)。
 */
const AREA_CSV_VALUE: Record<
  Exclude<AreaCode, AreaCode.UNSPECIFIED>,
  { ja: string; en: string }
> = {
  [AreaCode.HOKKAIDO]: { ja: '北海道', en: 'Hokkaido' },
  [AreaCode.TOHOKU]: { ja: '東北', en: 'Tohoku' },
  [AreaCode.TOKYO]: { ja: '東京', en: 'Tokyo' },
  [AreaCode.CHUBU]: { ja: '中部', en: 'Chubu' },
  [AreaCode.HOKURIKU]: { ja: '北陸', en: 'Hokuriku' },
  [AreaCode.KANSAI]: { ja: '関西', en: 'Kansai' },
  [AreaCode.CHUGOKU]: { ja: '中国', en: 'Chugoku' },
  [AreaCode.SHIKOKU]: { ja: '四国', en: 'Shikoku' },
  [AreaCode.KYUSHU]: { ja: '九州', en: 'Kyushu' },
  [AreaCode.OKINAWA]: { ja: '沖縄', en: 'Okinawa' },
}

/**
 * AreaCode → 表示言語の CSV のエリアの値。定義に無い値 (UNSPECIFIED 等) は空文字
 * (= 再アップロードでは必須列の行エラーになり、黙って別のエリアで登録されない)。
 */
export function areaCsvValue(area: AreaCode, isEnglish: boolean): string {
  if (area === AreaCode.UNSPECIFIED || !(area in AREA_CSV_VALUE)) return ''
  return AREA_CSV_VALUE[area][isEnglish ? 'en' : 'ja']
}

/**
 * `電圧階級` 列の値 (`conventions.md`「CSV の英語表示」: 特別高圧 = Extra-high voltage、
 * 高圧 = High voltage、低圧 = Low voltage)。backend `VoltageClassFromCsvLabel` が
 * 受理する値。
 */
const VOLTAGE_CLASS_CSV_VALUE: Record<
  Exclude<VoltageClass, VoltageClass.UNSPECIFIED>,
  { ja: string; en: string }
> = {
  [VoltageClass.EXTRA_HIGH]: { ja: '特別高圧', en: 'Extra-high voltage' },
  [VoltageClass.HIGH]: { ja: '高圧', en: 'High voltage' },
  [VoltageClass.LOW]: { ja: '低圧', en: 'Low voltage' },
}

/**
 * VoltageClass → 表示言語の CSV の電圧階級の値。定義に無い値は空文字 (= 再アップロード
 * では必須列の行エラーになり、黙って別の値で登録されない)。
 */
export function voltageClassCsvValue(
  voltageClass: VoltageClass,
  isEnglish: boolean,
): string {
  if (
    voltageClass === VoltageClass.UNSPECIFIED ||
    !(voltageClass in VOLTAGE_CLASS_CSV_VALUE)
  ) {
    return ''
  }
  return VOLTAGE_CLASS_CSV_VALUE[voltageClass][isEnglish ? 'en' : 'ja']
}
